import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable'
import React from 'react'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import BodyText from 'LuxKit/Typography/BodyText'
import { StyledSvgIcon } from 'LuxKit/SvgIcon'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import config from 'constants/config'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const SelectButton = styled(Clickable)`
  padding: ${rem(8)} ${rem(12)};
  height: 100%;
  width: 100%;
  transition: box-shadow 0.2s, background-color 0.2s, color 0.2s;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  position: relative;

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)};
  }

  > ${StyledSvgIcon} {
    color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &:focus {
    ${interactiveFocusStyle}
  }

  &.active {
    z-index: 1;
    box-shadow: inset 0 0 0 2px  ${props => props.theme.palette.brand.primary.normal};
  }

  &.invalid {
    box-shadow: inset 0 0 0 1px ${props => props.theme.palette.messaging.critical.normalForeground};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.default.four};

    > ${StyledSvgIcon} {
      color: ${props => props.theme.palette.neutral.default.four};
    }
  }
`

const Content = styled.div`
  flex-grow: 1;
  overflow: hidden;
`

interface Props extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean;
  /**
   * Whether or not the component is currently 'active'
   * In this context, that usually means that the select/dropdown related to it is open
   */
  active?: boolean;
  /**
   * The icon to be shown for this control
   */
  icon: React.ReactNode;
  /**
   * Whether or not this is being show in the mobile sidebar or not
   * This changes the arrow direction to be sideways instead
   */
  inSidebar?: boolean;
  /**
   * Whether or not this select box is in an invalid state
   * Will show the `invalidMessage` prop via the native pop up when validated
   */
  invalid?: boolean;
  /**
   * The label that identifies what this select box is for
   */
  label: string;
  /**
   * React node allowed so you define which parts of it can be bold via the `<b>` tag
   * Please avoid using other, more complex components
   */
  value?: React.ReactNode;
  /**
   * The value to show when there is no value set
   */
  placeholder: string;
  /**
   * Message to show when the input is invalid
   * Will appear in the native pop up on validation
   */
  invalidMessage?: string;
}

const OfferFilterSelectBox = React.forwardRef<HTMLButtonElement | HTMLDivElement, Props>((props, ref) => {
  const {
    label,
    value,
    placeholder,
    active,
    inSidebar,
    icon,
    invalid,
    className,
    invalidMessage,
    onClick,
    ...rest
  } = props

  const hasClick = !!onClick
  return (
    <SelectButton
      {...rest}
      onClick={onClick}
      as={hasClick ? undefined : 'div' }
      ref={ref as any}
      className={cn(
        className,
        {
          active,
          invalid,
        },
      )}
    >
      {invalid && <HiddenInput required invalidMessage={invalidMessage} />}
      {icon}
      <Content>
        <BodyText variant="small" wrap="truncate">{label}</BodyText>
        <BodyText variant="medium" colour={value ? 'neutral-two' : 'neutral-four'} wrap="truncate">
          {value || placeholder}
        </BodyText>
      </Content>
      {!config.businessTraveller.isEnabled && hasClick && <DropdownChevron
        direction={inSidebar ? 'right' : undefined}
        open={inSidebar ? undefined : active}
      />}
    </SelectButton>
  )
})

OfferFilterSelectBox.displayName = 'OfferFilterSelectBox'

export default OfferFilterSelectBox
